/**
 * Video Model Selector Component - Glass Morphism Design
 * Allows selection of video generation provider and model
 * Validates: Requirements 17.1
 */
import { useVideoStore, VideoProviderInfo, VideoModelInfo } from '../../stores/videoStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface VideoModelSelectorProps {
  disabled?: boolean;
}

export function VideoModelSelector({ disabled }: VideoModelSelectorProps) {
  const {
    providers,
    selectedProvider,
    selectedModel,
    setSelectedProvider,
    setSelectedModel,
  } = useVideoStore();

  const currentProvider = providers.find(p => p.id === selectedProvider);
  const currentModel = currentProvider?.models.find(m => m.id === selectedModel);

  return (
    <div className="flex items-center gap-3">
      {/* Provider Selector */}
      <div className="relative flex-1">
        <select
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e.target.value)}
          disabled={disabled || providers.length === 0}
          className="appearance-none w-full text-sm rounded-xl px-4 py-2.5 pr-10 border transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: THEME.glassLight,
            backdropFilter: 'blur(10px)',
            borderColor: THEME.border,
            color: THEME.textMain,
          }}
        >
          {providers.map((provider: VideoProviderInfo) => (
            <option key={provider.id} value={provider.id}>
              {provider.name}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <Icons.ChevronDown size={16} style={{ color: THEME.textMuted }} />
        </div>
      </div>

      {/* Model Selector */}
      <div className="relative flex-1">
        <select
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          disabled={disabled || !currentProvider}
          className="appearance-none w-full text-sm rounded-xl px-4 py-2.5 pr-10 border transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: THEME.glassLight,
            backdropFilter: 'blur(10px)',
            borderColor: THEME.border,
            color: THEME.textMain,
          }}
        >
          {currentProvider?.models.map((model: VideoModelInfo) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <Icons.ChevronDown size={16} style={{ color: THEME.textMuted }} />
        </div>
      </div>

      {/* Model Info Tooltip */}
      {currentModel && (
        <div
          className="hidden md:flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg"
          style={{
            backgroundColor: 'rgba(255,255,255,0.02)',
            color: THEME.textMuted,
          }}
        >
          <span>Max {currentModel.scriptLimit} chars</span>
          <span>•</span>
          <span>{currentModel.durationLimits.min}-{currentModel.durationLimits.max}s</span>
        </div>
      )}
    </div>
  );
}
